import { useState, useEffect } from "react";
import { ExternalLink, ChevronDown, ChevronRight, Copy, Check } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const METHOD_COLORS = {
  GET: "bg-emerald-600 text-white",
  POST: "bg-blue-600 text-white",
  PUT: "bg-amber-600 text-white",
  PATCH: "bg-orange-600 text-white",
  DELETE: "bg-red-600 text-white",
};

const CATEGORIES = [
  {
    tag: "auth",
    label: "Autenticación",
    description: "Login, registro, sesiones y OAuth",
    icon: "🔐",
  },
  {
    tag: "user",
    label: "Usuarios",
    description: "Perfiles, configuración y preferencias",
    icon: "👤",
  },
  {
    tag: "chat",
    label: "Chats & Mensajes",
    description: "Conversaciones, mensajes y adjuntos",
    icon: "💬",
  },
  {
    tag: "admin",
    label: "Administración",
    description: "Panel admin, gestión de plataforma",
    icon: "🛡️",
  },
  {
    tag: "business",
    label: "Negocios",
    description: "Dashboard y herramientas para empresas",
    icon: "🏢",
  },
  {
    tag: "professional",
    label: "Profesionales",
    description: "Dashboard para cuentas profesionales",
    icon: "💼",
  },
  {
    tag: "payments",
    label: "Pagos",
    description: "Links de pago, Stripe y transacciones",
    icon: "💳",
  },
  {
    tag: "ai",
    label: "Agentes IA",
    description: "LLM, STT, asistentes de voz e IA",
    icon: "🤖",
  },
];

const EndpointRow = ({ method, path, summary, description }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyPath = () => {
    navigator.clipboard.writeText(`${API_BASE}${path}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="border border-slate-700/50 rounded-xl overflow-hidden mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-3 hover:bg-slate-800/60 transition-colors text-left"
      >
        <span className={`text-xs font-bold px-2.5 py-1 rounded-lg shrink-0 min-w-[56px] text-center ${METHOD_COLORS[method] || "bg-slate-600 text-white"}`}>
          {method}
        </span>
        <span className="text-slate-200 font-mono text-sm flex-1 truncate">{path}</span>
        <span className="text-slate-400 text-xs hidden sm:block truncate max-w-[200px]">{summary}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
      </button>
      {open && (
        <div className="bg-slate-900/50 border-t border-slate-700/50 p-4 space-y-3">
          {summary && <p className="text-white font-medium">{summary}</p>}
          {description && <p className="text-slate-400 text-sm">{description}</p>}
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-slate-800 rounded-lg px-3 py-2 text-red-400 font-mono text-xs break-all">
              {API_BASE}{path}
            </code>
            <button
              onClick={copyPath}
              className="shrink-0 p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CategorySection = ({ category, endpoints }) => {
  const [open, setOpen] = useState(false);
  const filtered = endpoints.filter((e) =>
    e.tags?.some((t) => t.toLowerCase() === category.tag) ||
    e.path?.toLowerCase().includes(`/${category.tag}`)
  );

  if (filtered.length === 0) return null;

  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 bg-slate-800/60 hover:bg-slate-800 rounded-xl border border-slate-700/50 transition-colors mb-2 text-left"
      >
        <span className="text-2xl">{category.icon}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold">{category.label}</span>
            <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full">{filtered.length}</span>
          </div>
          <p className="text-slate-400 text-xs">{category.description}</p>
        </div>
        {open ? <ChevronDown className="w-5 h-5 text-slate-500" /> : <ChevronRight className="w-5 h-5 text-slate-500" />}
      </button>
      {open && (
        <div className="pl-2">
          {filtered.map((ep, i) => (
            <EndpointRow
              key={i}
              method={ep.method}
              path={ep.path}
              summary={ep.summary}
              description={ep.description}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ApiDocsPage = () => {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchSpec = async () => {
      try {
        const res = await fetch(`${API_BASE}/openapi.json`);
        if (!res.ok) throw new Error("No se pudo cargar la especificación");
        const spec = await res.json();
        const eps = [];
        for (const [path, methods] of Object.entries(spec.paths || {})) {
          for (const [method, data] of Object.entries(methods)) {
            if (["get", "post", "put", "patch", "delete"].includes(method)) {
              eps.push({
                path,
                method: method.toUpperCase(),
                summary: data.summary || "",
                description: data.description || "",
                tags: data.tags || [],
              });
            }
          }
        }
        setEndpoints(eps);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSpec();
  }, []);

  const filtered = search
    ? endpoints.filter(
        (e) =>
          e.path.toLowerCase().includes(search.toLowerCase()) ||
          e.summary.toLowerCase().includes(search.toLowerCase()) ||
          e.method.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-red-600/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        <div className="flex flex-col items-center text-center mb-10">
          <img src="/okchat-logo.png" alt="OkChat" className="h-16 w-auto mb-4 drop-shadow-lg" />
          <h1 className="text-4xl font-bold text-white mb-2">Documentación API</h1>
          <p className="text-slate-400 mb-6 max-w-lg">
            API completa de OkChat. Todos los endpoints para autenticación, mensajería, pagos y agentes IA.
          </p>
          <div className="flex gap-3 flex-wrap justify-center">
            <a
              href={`${API_BASE}/docs`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-medium transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Swagger UI Interactivo
            </a>
            <a
              href={`${API_BASE}/redoc`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-5 py-2.5 rounded-xl font-medium transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              ReDoc
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: "Endpoints", value: endpoints.length, color: "text-red-400" },
            { label: "GET", value: endpoints.filter((e) => e.method === "GET").length, color: "text-emerald-400" },
            { label: "POST", value: endpoints.filter((e) => e.method === "POST").length, color: "text-blue-400" },
            { label: "Versión", value: "1.0", color: "text-slate-300" },
          ].map((stat) => (
            <div key={stat.label} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-center">
              <p className={`text-2xl font-bold ${stat.color}`}>{loading ? "—" : stat.value}</p>
              <p className="text-slate-400 text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Buscar endpoint o método..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-slate-800/60 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-red-500/50"
          />
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4 text-red-400 text-sm mb-6">
            {error} — <a href={`${API_BASE}/docs`} className="underline hover:text-red-300">Usar Swagger UI</a>
          </div>
        )}

        {search && !loading && (
          <div>
            <p className="text-slate-400 text-sm mb-4">{filtered.length} resultado(s) para "{search}"</p>
            {filtered.map((ep, i) => (
              <EndpointRow key={i} {...ep} />
            ))}
          </div>
        )}

        {!search && !loading && endpoints.length > 0 && (
          <div>
            {CATEGORIES.map((cat) => (
              <CategorySection key={cat.tag} category={cat} endpoints={endpoints} />
            ))}
          </div>
        )}

        <div className="mt-12 pt-6 border-t border-slate-700/50 text-center text-slate-500 text-sm">
          <p>OkChat API v1.0 · <a href="https://okchat.okgo.mx" className="hover:text-slate-300">okchat.okgo.mx</a> · <a href="mailto:hola@okgo.mx" className="hover:text-slate-300">hola@okgo.mx</a></p>
        </div>
      </div>
    </div>
  );
};

export default ApiDocsPage;
